// 工作流程設計器工具函數

// 通用錯誤處理函數
export const handleApiError = (error, fallbackData, setter, errorMessage) => {
  console.error(errorMessage, error);
  setter(fallbackData);
};

// 變量引用語法處理工具函數
export const processVariableReferences = (text, processVariables) => {
  if (!text || !processVariables) return text;
  
  // 匹配 ${variable_name} 格式的變量引用
  const variablePattern = /\$\{([^}]+)\}/g;
  
  return text.replace(variablePattern, (match, variableName) => {
    const variable = processVariables.find(pv => pv.variableName === variableName.trim());
    if (variable) {
      // 返回變量的示例值或類型信息
      return `[${variable.variableName}: ${variable.dataType}]`;
    }
    return match; // 如果找不到變量，保持原樣
  });
};

// 生成唯一的任務名稱
export const generateUniqueTaskName = (baseName, existingNodes) => {
  const existingNames = existingNodes.map(node => node.data.taskName);
  let counter = 1;
  let newName = baseName;
  
  while (existingNames.includes(newName)) {
    counter++;
    newName = `${baseName} (${counter})`;
  }
  
  return newName;
};

// 生成 Webhook Token
export const generateWebhookToken = () => {
  const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  return token;
};

// 驗證工作流程連接邏輯
export const validateWorkflowLogic = (nodes, edges, t) => {
  const errors = [];
  const warnings = [];
  
  // 檢查是否有 Start 和 End 節點
  const hasStart = nodes.some(n => n.data.type === 'start');
  const hasEnd = nodes.some(n => n.data.type === 'end');
  
  if (!hasStart) {
    errors.push(t('workflowDesigner.missingStartNode'));
  }
  if (!hasEnd) {
    errors.push(t('workflowDesigner.missingEndNode'));
  }
  
  // 檢查是否有連接
  if (edges.length === 0) {
    errors.push(t('workflowDesigner.noConnections'));
    return { errors, warnings };
  }
  
  // 檢查循環連接
  // 只檢測直接的自循環（節點連接到自己），允許回到先前的節點
  const startNodes = nodes.filter(n => n.data.type === 'start');
  
  // 暫時移除循環檢測，允許任何連接模式
  // 如果需要，可以在這裡添加特定的循環檢測邏輯
  
  // 檢查 Start 節點是否有輸出
  for (const startNode of startNodes) {
    const hasOutput = edges.some(edge => edge.source === startNode.id);
    if (!hasOutput) {
      warnings.push(t('workflowDesigner.startNodeNoOutput', { nodeName: startNode.data.taskName || startNode.data.label }));
    }
  }
  
  // 檢查孤立節點（沒有輸入或輸出的節點）
  const connectedNodes = new Set();
  edges.forEach(edge => {
    connectedNodes.add(edge.source);
    connectedNodes.add(edge.target);
  });
  
  const isolatedNodes = nodes.filter(node => 
    node.data.type !== 'start' && 
    node.data.type !== 'end' && 
    !connectedNodes.has(node.id)
  );
  
  if (isolatedNodes.length > 0) {
    warnings.push(t('workflowDesigner.isolatedNodes', { nodeNames: isolatedNodes.map(n => n.data.taskName || n.data.label).join(', ') }));
  }
  
  // 檢查 End 節點是否有輸入
  const endNodes = nodes.filter(n => n.data.type === 'end');
  for (const endNode of endNodes) {
    const hasInput = edges.some(edge => edge.target === endNode.id);
    if (!hasInput) {
      warnings.push(t('workflowDesigner.endNodeNoInput', { nodeName: endNode.data.taskName || endNode.data.label }));
    }
  }
  
  return { errors, warnings };
};

// 獲取可用的輸出路徑（從當前節點出發的連線）
export const getAvailableOutputPaths = (nodeId, edges, nodes, t) => {
  if (!nodeId || !edges || !Array.isArray(edges) || !nodes || !Array.isArray(nodes)) {
    return [{
      id: 'default',
      label: t ? t('workflowDesigner.defaultPath') : 'Default Path',
      targetNodeId: null
    }];
  }
  
  const outgoingEdges = edges.filter(edge => edge.source === nodeId);
  const paths = outgoingEdges.map(edge => {
    const targetNode = nodes.find(n => n.id === edge.target);
    return {
      id: edge.id,
      label: targetNode ? targetNode.data.taskName || targetNode.data.label : `Node ${edge.target}`,
      targetNodeId: edge.target
    };
  });
  
  // 添加默認路徑選項
  paths.push({
    id: 'default',
    label: t ? t('workflowDesigner.defaultPath') : 'Default Path',
    targetNodeId: null
  });
  
  return paths;
};

/** 畫布上實際存在的 Handle ID（與 NodeTypes / useWorkflowState 一致），AI 可能產生 approved/rejected 等無效 ID */
const VALID_HANDLE_IDS = new Set([
  'left-source', 'left-target', 'right-source', 'right-target',
  'top-source', 'top-target', 'bottom-source', 'bottom-target'
]);

/**
 * 依節點位置推斷 edge 應使用的 sourceHandle / targetHandle，使連線方向與節點相對位置一致。
 * 用於載入流程或 AI 生成時未帶 handle 的 edge。Handle ID 與 useWorkflowState 的 getHandleMapping 一致。
 * @param {Array<{ id: string, position: { x: number, y: number } }>} nodes
 * @param {{ source: string, target: string }} edge
 * @returns {{ sourceHandle: string, targetHandle: string } | null}
 */
export const inferEdgeHandlesFromPositions = (nodes, edge) => {
  if (!nodes || !edge?.source || !edge?.target) return null;
  const sourceNode = nodes.find(n => n.id === edge.source);
  const targetNode = nodes.find(n => n.id === edge.target);
  if (!sourceNode?.position || !targetNode?.position) return null;
  const sx = sourceNode.position.x ?? 0;
  const sy = sourceNode.position.y ?? 0;
  const tx = targetNode.position.x ?? 0;
  const ty = targetNode.position.y ?? 0;
  const dx = tx - sx;
  const dy = ty - sy;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0
      ? { sourceHandle: 'right-source', targetHandle: 'left-target' }
      : { sourceHandle: 'left-source', targetHandle: 'right-target' };
  }
  return dy >= 0
    ? { sourceHandle: 'bottom-source', targetHandle: 'top-target' }
    : { sourceHandle: 'top-source', targetHandle: 'bottom-target' };
};

/**
 * 若 edge 的 sourceHandle/targetHandle 不在畫布實際 Handle ID 內（例如 AI 產生的 "approved"/"rejected"），
 * 改為依節點位置推斷，避免連線不顯示卻仍佔用連線導致「已經存在連接」。
 * @param {Array} nodes
 * @param {object} edge
 * @returns {{ sourceHandle: string, targetHandle: string }}
 */
export const normalizeEdgeHandles = (nodes, edge) => {
  const hasValidHandles =
    edge.sourceHandle && VALID_HANDLE_IDS.has(edge.sourceHandle) &&
    edge.targetHandle && VALID_HANDLE_IDS.has(edge.targetHandle);
  if (hasValidHandles) return { sourceHandle: edge.sourceHandle, targetHandle: edge.targetHandle };
  const inferred = inferEdgeHandlesFromPositions(nodes, edge);
  return inferred || { sourceHandle: 'right-source', targetHandle: 'left-target' };
};

/**
 * 為 edge 產生唯一 id，供 React Flow 正確渲染。無 id 或 id 不完整時會導致連線存在於 state 但畫面不顯示。
 * @param {{ source: string, target: string }} edge
 * @param {{ sourceHandle: string, targetHandle: string }} handles
 * @param {number} index - 同 source+target+handles 多條時用於區分
 */
export const getOrCreateEdgeId = (edge, handles, index = 0) => {
  if (edge?.id && typeof edge.id === 'string' && edge.id.trim()) return edge.id;
  const { source, target } = edge || {};
  const sh = handles?.sourceHandle || 'right-source';
  const th = handles?.targetHandle || 'left-target';
  const suffix = index > 0 ? `-${index}` : '';
  return `xy-edge__${source || 's'}${sh}-${target || 't'}${th}${suffix}`;
};

/**
 * 將解析後的流程 JSON（nodes/edges）還原為畫布可用的 state（圖示、handle、onDelete / onEdgeSwitch）。
 * 與從後端載入流程時的處理一致，供檔案匯入與 API 載入共用。
 */
export const hydrateFlowFromParsedJson = (flow, nodeTypes, handleDeleteNode, onEdgeSwitch) => {
  if (!flow || !Array.isArray(flow.nodes)) {
    throw new Error('INVALID_FLOW');
  }
  for (const node of flow.nodes) {
    if (!node?.data?.type) {
      throw new Error('INVALID_NODE');
    }
  }
  const edgesInput = Array.isArray(flow.edges) ? flow.edges : [];

  const nodesWithIcons = flow.nodes.map((node) => {
    const nodeType = nodeTypes.find((n) => n.type === node.data.type);
    let reactFlowType;
    if (node.data.type === 'start') {
      reactFlowType = 'input';
    } else if (node.data.type === 'end') {
      reactFlowType = 'output';
    } else {
      reactFlowType = 'default';
    }
    return {
      ...node,
      type: reactFlowType,
      data: {
        ...node.data,
        icon: nodeType ? nodeType.icon : null,
        iconColor: nodeType ? nodeType.iconColor : null,
        label: node.data.label || nodeType?.label || node.data.type,
        taskName: node.data.taskName || node.data.label || nodeType?.label || node.data.type,
        onDelete: node.data.type === 'start' ? null : handleDeleteNode,
      },
    };
  });

  const edgesWithStyle = edgesInput.map((edge, idx) => {
    const handles = normalizeEdgeHandles(nodesWithIcons, edge);
    const { sourceHandle, targetHandle } = handles;
    return {
      ...edge,
      id: getOrCreateEdgeId(edge, handles, idx),
      sourceHandle,
      targetHandle,
      type: edge.type || 'smoothstep',
      animated: edge.animated !== undefined ? edge.animated : true,
      style: edge.style || { strokeWidth: 3, stroke: '#1890ff' },
      markerEnd: edge.markerEnd || { type: 'arrowclosed', width: 12, height: 12, color: '#1890ff' },
      data: { ...(edge.data || {}), onEdgeSwitch: onEdgeSwitch },
    };
  });

  return { nodes: nodesWithIcons, edges: edgesWithStyle };
};
